
import { PAGE_TYPE } from 'interfaces';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRef } from 'react';
import styled from 'styled-components';
import nextI18NextConfig from '../../next-i18next.config.js';
import BackgroundImage from '@/components/molecules/BackgroundImage';
import Button from '@/components/molecules/Button.jsx';
import GridList from '@/components/molecules/GridList';
import SectionText from '@/components/molecules/SectionText';
import Title from '@/components/molecules/Title.jsx';

import IntroSection from '@/components/wrappers/IntroSection';
import PageWrapper from '@/components/wrappers/PageWrapper';
import Section from '@/components/wrappers/Section';
import SplitSection from '@/components/wrappers/SplitSection';

import AppStoreBadge from '@/public/img/AppStoreBadge';
import { renderScript, renderUnderline } from '@/utilities/functions';

// #region STYLE
const BadgeContainer = styled.span<{ inSection?: boolean }>`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	margin: ${props => props.inSection ? '2em 0 0' : '3em 0 1em'};
	a {
		display: inline-block;
		width: 10em;
		text-decoration: none;
		outline: 0;
		transition: .25s;
		img {
			width: ${props => props.inSection ? '80%' : '100%'};
		}
		svg {
			width: ${props => props.inSection ? '65%' : '80%'};
		}

		:hover {
			transform: scale(.96);
		}
	}
`;

const TitlesContainer = styled.div`
  text-align: center;
`;
// #endregion

// #region COMPONENT
const DownloadAppTalent = ({ currentPath, currentLang }: { currentPath: string, currentLang: string }) => {
	const { t } = useTranslation('downloadAppTalent');
	const scrollTo = useRef();

	const DownloadLandingPage = 'https://ressources.bruce.work/contact/dl-app';
	const GooglePlayLink = 'https://app.adjust.com/mhrod40?label=Playstore&redirect=https%3A%2F%2Fplay.google.com%2Fstore%2Fapps%2Fdetails%3Fid%3Dbruce.team';
	const AppleStoreLink = 'https://app.adjust.com/e3j5vyq?redirect=https%3A%2F%2Fapps.apple.com%2Ffr%2Fapp%2Fbruce-offres-demploi%2Fid1062629311';

	// #region RENDER
	return (
		<PageWrapper
			title={t('meta.title')}
			description={t('meta.description')}
			introImageUrl='/src/public/img/downloadAppTalent.jpg'
			alternateUrl={{ fr: '', en: '', es: '' }}
			currentPath={currentPath}
			currentLang={currentLang}
			pageType={PAGE_TYPE.b2c}
		>
			<Head>
				{renderScript([
					{
						'@context': 'https://schema.org',
						'@type': 'SoftwareApplication',
						'aggregateRating': {
							'@type': 'AggregateRating',
							'ratingCount': '282',
							'ratingValue': '4.2',
						},
						'applicationCategory': 'BusinessApplication',
						'name': 'Bruce',
						'offers': {
							'@type': 'Offer',
							'price': '0',
							'priceCurrency': 'EUR',
						},
						'operatingSystem': [
							'IOS',
							'ANDROID',
						],
					},
					{
						'@context': 'https://schema.org',
						'@type': 'BreadcrumbList',
						'itemListElement': [{
							'@type': 'ListItem',
							'item': 'https://www.bruce.work/',
							'name': 'Accueil',
							'position': 1,
						}, {
							'@type': 'ListItem',
							'item': 'https://www.bruce.work/trouver-un-emploi/',
							'name': 'Trouver un emploi',
							'position': 2,
						}, {
							'@type': 'ListItem',
							'item': 'https://www.bruce.work/trouver-un-emploi/telecharger-application-talent',
							'name': "Télécharger l'application",
							'position': 3,
						}],
					},
				])}
			</Head>
			<IntroSection background='/img/downloadAppTalent.jpg' underlineColor='red' scrollTo={scrollTo}>
				<h1>
					{renderUnderline(t('introSection.h1', { returnObjects: true }) as any)}
				</h1>
				<h2>{t('introSection.h2')}</h2>
				<BadgeContainer>
					<a aria-label='Appstore badge' href={AppleStoreLink} target='_blank' rel='noopener, noreferrer' >
						<AppStoreBadge />
					</a>
					<a aria-label='GooglePlay badge' href={GooglePlayLink} target='_blank' rel='noopener, noreferrer' >
						<img src='/src/public/img/google-play-badge.png' alt='google play badge' />
					</a>
				</BadgeContainer>
			</IntroSection>
			<SplitSection fixedHeight bgColor='beige' scrollTo={scrollTo} firstWidth={15} secondWidth={40} thirdWidth={44}>
				<div />
				<SectionText>
					<Title underlineColor='red'>
						{renderUnderline(t('secondSection.title', { returnObjects: true }) as any)}
					</Title>
					<p>
						{t('secondSection.text')}
					</p>
					<Button
						href={DownloadLandingPage}
						name={t('secondSection.button.name')}
						openOnNewTab
						version='blackWhite'
					/>
				</SectionText>
				<BackgroundImage url='/img/downloadapp-hand_2.png' />
			</SplitSection>
			<Section>
				<TitlesContainer>
					<Title underlineColor='red'>
						{renderUnderline(t('benefits.title', { returnObjects: true }) as any)}
					</Title>
					<h3> {t('benefits.h3')} </h3>
				</TitlesContainer>
				<GridList content={t('benefits.list', { returnObjects: true }) as any} />
			</Section>
			<SplitSection fixedHeight firstWidth={50} secondWidth={50}>
				<SectionText>
					<Title underlineColor='red'>
						{renderUnderline(t('lastSection.title', { returnObjects: true }) as any)}
					</Title>
					<p>
						{t('lastSection.text')}
					</p>
					<Button
						href={DownloadLandingPage}
						name={t('secondSection.button.name')}
						openOnNewTab
						version='whiteRed'
					/>
				</SectionText>
				<BackgroundImage url='/img/downloadApp_administration.jpg' hideOnMobile />
			</SplitSection>
		</PageWrapper>
	);
	// #endregion
};
// #endregion

// #region CONNEXION
export const getServerSideProps = async ({ query }: { query: any }) => {
	return {
		props: {
			...await serverSideTranslations(query.language, ['downloadAppTalent', 'navbar', 'footer'], nextI18NextConfig),
		},
	};
};
// #endregion

export default DownloadAppTalent;
